"use client"

import { createContext, useContext, useState, type ReactNode } from "react"

interface SceneState {
  ambientIntensity: number
  directionalIntensity: number
  directionalPosition: [number, number, number]
  modelColor: string
  modelOpacity: number
  wireframe: boolean
  loadedModel: string | null
  cameraPosition: [number, number, number]
  debugMode: boolean
  renderQuality: "low" | "medium" | "high" | "ultra"
  enablePostProcessing: boolean
  enableShadows: boolean
  enableReflections: boolean
  enableHeatmap: boolean
}

interface SceneContextType {
  sceneState: SceneState
  updateSceneState: (updates: Partial<SceneState>) => void
  resetSceneState: () => void
}

const defaultSceneState: SceneState = {
  ambientIntensity: 0.4,
  directionalIntensity: 1.0,
  directionalPosition: [10, 10, 5],
  modelColor: "#ffffff",
  modelOpacity: 1.0,
  wireframe: false,
  loadedModel: null,
  cameraPosition: [5, 5, 5],
  debugMode: false,
  renderQuality: "medium",
  enablePostProcessing: true,
  enableShadows: true,
  enableReflections: true,
  enableHeatmap: true,
}

const SceneContext = createContext<SceneContextType | undefined>(undefined)

export function SceneProvider({ children }: { children: ReactNode }) {
  const [sceneState, setSceneState] = useState<SceneState>(defaultSceneState)

  const updateSceneState = (updates: Partial<SceneState>) => {
    setSceneState((prev) => ({ ...prev, ...updates }))
  }

  const resetSceneState = () => {
    setSceneState(defaultSceneState)
  }

  return (
    <SceneContext.Provider value={{ sceneState, updateSceneState, resetSceneState }}>{children}</SceneContext.Provider>
  )
}

export function useScene() {
  const context = useContext(SceneContext)
  if (!context) {
    throw new Error("useScene must be used within SceneProvider")
  }
  return context
}
